import { useRouter } from '@abyss/web/hooks/useRouter';
import { storage } from '@abyss/web/tools/storage';
import { Badge } from '@abyss/web/ui/Badge';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { find } from 'lodash';
import { useTranslation } from 'react-i18next';

import { ProviderType } from '../../models/ProviderDetails';
import { getLagoonConfigValue } from '../../utils/providerSearch.utils';
import { loginNotRequired } from '../../utils/session.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import {
  CareCategories,
  Constants,
  CoverageTypesCodes,
  ReverseCoverageTypesCodes,
} from '../Constants';
import { ConstantsRoutes } from '../ConstantsRoutes';
import { convertObjectToUrlParams } from '../Utils';
import {
  CareTeamCardHeader,
  CareTeamCardLink,
  CareTeamCardTitle,
} from './CareTeamCard.styled';
import { OutOfNetworkLabelCss, PcpBadge } from './CareTeamUtils.styled';

const OutOfNetworkLabel = ({ providerName, msg }) => (
  <OutOfNetworkLabelCss
    data-testid={`out-of-network-label-${providerName
      ?.replace(/\s+/g, '-')
      .toLowerCase()}`}
  >
    {msg}
  </OutOfNetworkLabelCss>
);

export const PcpNetworkStatusBadge = ({
  isPcp,
  isOutOfDirectory,
  providerName,
  suppressed,
  oon,
  goingOON,
}) => {
  const { t } = useTranslation();
  if (!isPcp) return null;

  return (
    <>
      <Badge
        className={PcpBadge.className}
        data-testid={`pcp-badge-${providerName
          ?.replace(/\s+/g, '-')
          .toLowerCase()}`}
        outline
        variant="success"
      >
        {t('PCP')}
      </Badge>
      {suppressed ? null : goingOON ? (
        <Badge
          data-testid={`going-out-of-network-badge-${providerName
            ?.replace(/\s+/g, '-')
            .toLowerCase()}`}
          outline
          style={{
            margin: '4px 0px',
          }}
          variant="warning"
        >
          <IconSymbol
            color="$warning1"
            css={{
              verticalAlign: 'middle',
            }}
            icon="warning"
            size="14"
          />{' '}
          {t('Going out-of-network')}
        </Badge>
      ) : oon || isOutOfDirectory ? (
        <Badge
          data-testid={`out-of-network-badge-${providerName
            ?.replace(/\s+/g, '-')
            .toLowerCase()}`}
          outline
          style={{
            margin: '4px 0px',
          }}
          variant="warning"
        >
          <IconSymbol
            color="$warning1"
            css={{
              verticalAlign: 'middle',
            }}
            icon="warning"
            size="14"
          />{' '}
          {t('Out of network')}
        </Badge>
      ) : null}
    </>
  );
};

export const OutOfNetworkBadge = ({
  msg,
  providerName,
  type = 'badge',
  isGoingOutOfNetwork = false,
}) => {
  if (
    isGoingOutOfNetwork ||
    msg === 'Out-of-network' ||
    msg === 'Out of network'
  ) {
    return (
      <Badge
        data-testid={`out-of-network-badge-${providerName
          ?.replace(/\s+/g, '-')
          .toLowerCase()}`}
        key={msg}
        outline
        style={{
          margin: '4px 0px',
        }}
        variant="warning"
      >
        <IconMaterial
          color="$warning1"
          css={{
            verticalAlign: 'middle',
          }}
          icon="warning"
          size="14"
        />{' '}
        {msg}
      </Badge>
    );
  }

  if (type === 'badge') {
    return (
      <Badge
        data-testid={`out-of-network-badge-${providerName
          ?.replace(/\s+/g, '-')
          .toLowerCase()}`}
        outline
        style={{ margin: '4px 0px', fontWeight: 600 }}
        variant="neutral"
      >
        {msg}
      </Badge>
    );
  }

  return <OutOfNetworkLabel msg={msg} providerName={providerName} />;
};

export const getSpeciality = (provider) => {
  if (!provider?.specialities || provider?.specialities.length === 0) {
    return '';
  }
  const isSpeciality = provider?.specialities
    ?.map((speciality) => speciality)
    .join(', ');

  return isSpeciality;
};
export const resolveLinkForDetailPage = (provider, chipStore) => {
  const { setChipValue, setOriginalChipValue, setCoverageType } =
    chipStore || {};
  const providerId = provider?.providerId || '';
  const showDirection = provider?.showDirection || false;
  const locationId =
    Array.isArray(provider?.providerLocations) &&
    provider?.providerLocations.length > 0
      ? provider?.providerLocations[0]?.locationId
      : '';
  const isTiered = provider?.providerLocations?.isTieredProvider || false;
  const chipValue =
    provider?.originalChipValue ||
    provider?.chipValue ||
    CareCategories.PRIMARY_CARE;

  setChipValue(chipValue);
  setOriginalChipValue(chipValue);
  const coverageType =
    ReverseCoverageTypesCodes[
      find(Constants.MOCK_CARE_CATEGORIES, {
        value: chipValue,
      })?.coverageType || CoverageTypesCodes.MEDICAL
    ];
  setCoverageType(coverageType);
  const searchRadius = provider?.searchRadius || 5;
  const urlParams = convertObjectToUrlParams(chipStore, {
    coverageType,
    providerId,
    showDirection,
    locationId,
    isTieredSnackCard: isTiered,
    searchRadius,
    chipValue,
  });

  let path;
  if (provider.providerType === ProviderType.PRACTITIONER) {
    path = `${ConstantsRoutes.PROVIDER_DETAILS.path}${urlParams}`;
  } else if (provider.providerType === ProviderType.MEDICAL_GROUP) {
    path = `${ConstantsRoutes.PROVIDER_GROUP_DETAILS.path}${urlParams}`;
  } else path = `${ConstantsRoutes.FACILITY_DETAILS.path}${urlParams}`;

  return path;
};

export const providerName = (provider) => {
  const providerName = provider?.providerName || '';
  const firstName = provider?.firstName || '';
  const middleName = provider?.middleName || '';
  const lastName = provider?.lastName || '';
  return providerName !== ''
    ? providerName
    : `${firstName} ${middleName} ${lastName}`;
};

export const CareTeamCardHeaderSection = (_css?) => {
  const { t } = useTranslation();
  const { navigate } = useRouter();

  return (
    <CareTeamCardHeader css={_css.css}>
      <CareTeamCardTitle css={{ width: '65%' }}>
        {' '}
        {t('CARE_TEAM.CARE_TEAM_BREADCRUMB')}
      </CareTeamCardTitle>
      <CareTeamCardLink
        aria-label="View all & edit Care team"
        css={{ width: 'fit-content' }}
        data-auto-testid="view-all-providers-link"
        data-testid="view-all-providers-link-care-team"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          navigate(`${ConstantsRoutes.CARE_TEAM.path}`);
          adobeLinkTrackEvent({
            name: ANALYTICS_CONSTANTS.VIEW_ALL,
            location: ANALYTICS_CONSTANTS.CARE_TEAM_SNACK_CARD,
            type: ANALYTICS_CONSTANTS.INTERNAL_TYPE,
          });
        }}
      >
        {t('View all')} & {t('Edit')}
      </CareTeamCardLink>
    </CareTeamCardHeader>
  );
};

export const filterCareTeamChipsByCoverageType = (
  careTeamChips,
  memberInfo
) => {
  if (!careTeamChips || careTeamChips.length === 0) {
    return [];
  }
  Constants.COVERAGE_TYPECODE_TO_CHIP.B = CoverageTypesCodes.BEHAVIORAL_HEALTH;
  const memberCoverage: string[] = memberInfo?.eligibility
    .flatMap((coverage) => coverage?.memberHealthCoverage?.coverageType)
    .map(
      (coverageType) =>
        Constants.COVERAGE_TYPECODE_TO_CHIP[
          coverageType?.typeCode?.coverageTypeCode || ''
        ]
    );
  const result = Constants.MOCK_CARE_CATEGORIES.filter(
    (category) =>
      memberCoverage.includes(category.coverageType) &&
      careTeamChips.find((chip) => CareCategories[chip] === category.value)
        ?.length > 0
  ).map((category) =>
    careTeamChips.find((chip) => CareCategories[chip] === category.value)
  );
  return result;
};

export const ANALYTICS_CONSTANTS = {
  BOOKMARK_ICON: 'bookmark icon',
  PROVIDER_SNACK_CARD: 'provider snack card',
  VIEW_ALL: 'view all & edit',
  INTERNAL_TYPE: 'internal',
  CARE_TEAM_SNACK_CARD: 'care team snack card',
  CHANGE_YOUR_PCP: 'change your pcp',
  EDIT_PCP_DROPDOWN: 'edit pcp dropdown',
  PCP_SNACK_CARD: 'pcp snack card',
  CHANGE_YOUR_PCP_LOCATION: 'change your pcp location',
  CARE_TEAM: 'care team',
  SORT_BY: 'body: sort by',
  SORT_RESULTS: 'sort results',
};

export const getIsSuperUser = (allConfigs) =>
  loginNotRequired() ? false : isUhcSuperUser(allConfigs);

export const isUhcSuperUser = (allConfigs) => {
  const hostname = window?.location?.hostname?.toLowerCase() ?? '';
  const isSuperUserFromSession = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.SUPER_USER
  );
  if (isSuperUserFromSession !== null && isSuperUserFromSession !== undefined) {
    return isSuperUserFromSession;
  }
  const includedHostNamesForSuperUser = getLagoonConfigValue(
    allConfigs,
    'INCLUDED_HOST_NAMES_FOR_SUPER_USER'
  );
  if (
    !includedHostNamesForSuperUser
      .split(',')
      .filter((s) => s && s.trim() !== '')
      .some((host) => hostname.includes(host))
  ) {
    return false;
  }
  return null;
};
